/**
 * @file 试卷分页
 * @description 按内容区高度把题目块拆到多页，行为对齐客户端三套打印模板。
 *
 * @whenToUse 教案卷、阶段卷、精简教案卷生成预览时。
 * @whenNotToUse 不需要按 A4 拆页的展示。
 */

import { flushSync } from 'react-dom'
import type { BlockTypeName, ColumnItem, PaperBlock } from '@/components/pages/handout-page/print/blocks.tsx'
import {
  measureStableElementHeight,
  pruneEmptyPaperPages,
  resolvePaperQuestionContainerHeight,
  waitForBlockImagesLoaded,
} from '@/components/pages/handout-page/print/measure.ts'

/** 分页模式，对应三套模板的拆页规则 */
export type PaginateMode = 'knowledge' | 'lite' | 'stage'

const QUESTION_TYPES = new Set<BlockTypeName>([
  'coreKnowledgePoints',
  'consolidationQuestions',
  'learnWithMeList',
  'tryPracticeQuestions',
  'adaptivePracticeQuestions',
])

const MAX_PAGES = 80

/**
 * 复制分页块，避免拆页时改到调用方对象。
 * @param block 原块
 */
function cloneBlock(block: PaperBlock): PaperBlock {
  return {
    ...block,
    items: block.items?.map((item) => ({
      ...item,
      options: item.options ? item.options.map((option) => ({ ...option })) : item.options,
    })),
    data: block.data
      ? {
          ...block.data,
          options: block.data.options
            ? block.data.options.map((option) => ({ ...option }))
            : block.data.options,
        }
      : undefined,
  }
}

/**
 * 把 HTML 拆成顶层节点，供跨页二分。
 * @param html 富文本
 */
function htmlFragments(html: string): string[] {
  const trimmed = html.trim()
  if (!trimmed) return []
  const doc = new DOMParser().parseFromString(`<body>${trimmed}</body>`, 'text/html')
  const children = Array.from(doc.body.children)
  if (children.length > 0) return children.map((el) => el.outerHTML)
  return [trimmed]
}

/**
 * 是否为题目内容块。
 * @param block 分页块
 */
function isQuestionContentBlock(block: PaperBlock): boolean {
  return QUESTION_TYPES.has(block.type)
}

/**
 * 题目是否能按顶层节点拆开。
 * @param block 分页块
 */
function canSplitQuestion(block: PaperBlock): boolean {
  if (block.type === 'tryPracticeQuestions' || block.type === 'adaptivePracticeQuestions') {
    return htmlFragments(block.data?.question || '').length >= 2
  }
  if (
    block.type !== 'coreKnowledgePoints' &&
    block.type !== 'consolidationQuestions' &&
    block.type !== 'learnWithMeList'
  ) {
    return false
  }
  return (block.items ?? []).some((item) => htmlFragments(item.html).length >= 2)
}

/**
 * 答案讲解是否能拆开。
 * @param block 分页块
 */
function canSplitAnswer(block: PaperBlock): boolean {
  return block.type === 'answer-data' && htmlFragments(block.data?.analysisHtml || '').length >= 2
}

/**
 * 精简模板典例讲解是否能拆开。
 * @param block 分页块
 */
function canSplitLearnVertical(block: PaperBlock): boolean {
  return (
    block.type === 'learn-with-me-vertical' &&
    htmlFragments(block.data?.analysisHtml || '').length >= 2
  )
}

/**
 * 按页测高并写入页面。
 */
export class PaperPaginator {
  /** 当前各页内容 */
  pages: PaperBlock[][] = [[]]
  /** 正在写入的页下标 */
  index = 0
  /** 内容区可用高度 */
  containerHeight = 0

  constructor(
    private mode: PaginateMode,
    private commitPages: (pages: PaperBlock[][]) => void,
    private getContainer: (index: number) => HTMLElement | null,
    private getRoot: () => HTMLElement | null,
  ) {}

  /**
   * 把当前页同步到 React，方便下一帧测高。
   */
  private commit() {
    if (this.pages.length > MAX_PAGES) {
      throw new Error(`打印页数异常（${this.pages.length} 页），已停止分页`)
    }
    const snapshot = this.pages.map((page) => page.slice())
    flushSync(() => {
      this.commitPages(snapshot)
    })
  }

  /**
   * 测当前页内容高度。
   */
  private async measureCurrent(): Promise<number> {
    return measureStableElementHeight(this.getContainer(this.index))
  }

  /**
   * 临时放入一块并测整页高度，随后移除。
   * @param block 待测块
   */
  private async heightWith(block: PaperBlock): Promise<number> {
    this.pages[this.index].push(block)
    await waitForBlockImagesLoaded(block)
    this.commit()
    const height = await this.measureCurrent()
    this.pages[this.index].pop()
    this.commit()
    return height
  }

  /**
   * 准备第一页并量出内容区高度。
   */
  async prepare() {
    this.pages = [[]]
    this.index = 0
    this.commit()
    this.containerHeight = await resolvePaperQuestionContainerHeight(this.getRoot())
  }

  /**
   * 答案区新起一页。
   */
  async breakPage() {
    await this.pushPage()
  }

  /**
   * 新开一页。
   */
  private async pushPage() {
    this.pages.push([])
    this.index += 1
    this.commit()
  }

  /**
   * 写入一块；放不下时按模板规则拆页或拆内容。
   * @param raw 分页块
   * @param splitQuestionOnCurrentPage 当前页剩余空间不足时仍拆题目
   */
  async add(raw: PaperBlock, splitQuestionOnCurrentPage = false) {
    const block = cloneBlock(raw)
    this.pages[this.index].push(block)
    await waitForBlockImagesLoaded(block)
    this.commit()
    const height = await this.measureCurrent()
    if (height <= this.containerHeight) return

    this.pages[this.index].pop()
    this.commit()

    if (canSplitAnswer(block)) {
      await this.splitAnswer(block)
      return
    }
    if (this.mode === 'lite' && canSplitLearnVertical(block)) {
      await this.splitLearnVertical(block)
      return
    }
    if (
      this.mode === 'knowledge' &&
      canSplitQuestion(block) &&
      (block.fullRow || splitQuestionOnCurrentPage || this.pages[this.index].length === 0)
    ) {
      if (block.type === 'tryPracticeQuestions' || block.type === 'adaptivePracticeQuestions') {
        await this.splitQuestionDetail(block)
      } else {
        await this.splitQuestionColumn(block)
      }
      return
    }
    await this.continueWithoutSplit(block)
  }

  /**
   * 放不下且不能按内容拆开时换页，标题与题目尽量留在同一页。
   * @param block 当前块
   */
  private async continueWithoutSplit(block: PaperBlock) {
    const paper = this.pages[this.index]
    if (paper.length === 0) {
      this.pages[this.index].push(block)
      await waitForBlockImagesLoaded(block)
      this.commit()
      await this.measureCurrent()
      return
    }

    const prev = paper[paper.length - 1]
    if (prev.type === 'block_title' || prev.type === 'answer-subtitle') {
      if (this.mode === 'knowledge' && prev.type === 'block_title' && isQuestionContentBlock(block)) {
        const currentPage = this.pages[this.index]
        currentPage.pop()
        const trialPage = [prev, { ...block, isLast: true }]
        this.pages.push(trialPage)
        const trialIndex = this.pages.length - 1
        const previousIndex = this.index
        this.index = trialIndex
        this.commit()
        const titleAndQuestionHeight = await this.measureCurrent()
        this.pages.pop()
        this.index = previousIndex
        this.commit()
        if (titleAndQuestionHeight <= this.containerHeight || !canSplitQuestion(block)) {
          await this.pushPage()
          await this.add(prev)
          await this.add(block)
          return
        }
        currentPage.push(prev)
      }
      this.pages[this.index].pop()
      await this.pushPage()
      await this.add(prev)
      await this.add(block, this.mode === 'knowledge' && isQuestionContentBlock(block))
      return
    }

    await this.pushPage()
    block.isLast = true
    await this.add(block)
  }

  /**
   * 答案讲解按顶层节点拆到后续页。
   * @param block 答案块
   */
  private async splitAnswer(block: PaperBlock) {
    await this.splitHtmlField(block, 'analysisHtml', (source, html, continuation) => ({
      ...source,
      isLast: continuation ? true : source.isLast,
      data: {
        ...source.data,
        analysisHtml: html,
        analysisContinuation: continuation,
      },
    }))
  }

  /**
   * 精简模板的典例讲解跨页。
   * @param block 典例块
   */
  private async splitLearnVertical(block: PaperBlock) {
    await this.splitHtmlField(block, 'analysisHtml', (source, html, continuation) => ({
      ...source,
      isLast: continuation ? true : source.isLast,
      data: {
        ...source.data,
        analysisHtml: html,
        analysisContinuation: continuation,
      },
    }))
  }

  /**
   * 按讲解字段二分，当前页放得下的部分留下，其余续到下一页。
   * @param block 原块
   * @param field 被拆的 HTML 字段
   * @param build 生成当前片段块
   */
  private async splitHtmlField(
    block: PaperBlock,
    field: 'analysisHtml',
    build: (source: PaperBlock, html: string, continuation: boolean) => PaperBlock,
  ) {
    const nodes = htmlFragments(String(block.data?.[field] || ''))
    if (nodes.length < 2) {
      await this.continueWithoutSplit(block)
      return
    }

    let lo = 1
    let hi = nodes.length
    let best = 0
    while (lo <= hi) {
      const mid = (lo + hi) >> 1
      const trial = build(block, nodes.slice(0, mid).join(''), false)
      const height = await this.heightWith(trial)
      if (height <= this.containerHeight) {
        best = mid
        lo = mid + 1
      } else {
        hi = mid - 1
      }
    }

    if (best === 0) {
      if (this.pages[this.index].length > 0) {
        await this.pushPage()
        await this.splitHtmlField(block, field, build)
        return
      }
      this.pages[this.index].push(block)
      await waitForBlockImagesLoaded(block)
      this.commit()
      await this.measureCurrent()
      return
    }

    let use = best
    let first = build(block, nodes.slice(0, use).join(''), false)
    while (use >= 1) {
      this.pages[this.index].push(first)
      await waitForBlockImagesLoaded(first)
      this.commit()
      const height = await this.measureCurrent()
      if (height <= this.containerHeight) break
      if (use <= 1) {
        if (this.pages[this.index].length > 1) {
          this.pages[this.index].pop()
          this.commit()
          await this.pushPage()
          await this.splitHtmlField(block, field, build)
          return
        }
        break
      }
      this.pages[this.index].pop()
      this.commit()
      use -= 1
      first = build(block, nodes.slice(0, use).join(''), false)
    }

    const rest = nodes.slice(use).join('')
    if (!rest.trim()) return
    await this.pushPage()
    await this.add(build(block, rest, true))
  }

  /**
   * 普通题目按顶层节点连续分页。
   * @param block 题目块
   */
  private async splitQuestionDetail(block: PaperBlock) {
    const nodes = htmlFragments(block.data?.question || '')
    let start = 0
    let firstPart = true
    while (start < nodes.length) {
      let lo = start + 1
      let hi = nodes.length
      let best = start
      while (lo <= hi) {
        const mid = (lo + hi) >> 1
        const trial = this.buildQuestionDetail(block, nodes.slice(start, mid).join(''), !firstPart, mid === nodes.length)
        const height = await this.heightWith(trial)
        if (height <= this.containerHeight) {
          best = mid
          lo = mid + 1
        } else {
          hi = mid - 1
        }
      }
      if (best === start) {
        if (this.pages[this.index].length > 0) {
          await this.pushPage()
          continue
        }
        best = start + 1
      }
      const piece = this.buildQuestionDetail(
        block,
        nodes.slice(start, best).join(''),
        !firstPart,
        best === nodes.length,
      )
      this.pages[this.index].push(piece)
      await waitForBlockImagesLoaded(piece)
      this.commit()
      await this.measureCurrent()
      start = best
      firstPart = false
      if (start < nodes.length) await this.pushPage()
    }
  }

  /**
   * 生成普通题目的一页片段。
   * @param block 原题
   * @param question 当前页题干
   * @param continuation 是否续页
   * @param isFinal 是否最后一段
   */
  private buildQuestionDetail(
    block: PaperBlock,
    question: string,
    continuation: boolean,
    isFinal: boolean,
  ): PaperBlock {
    return {
      ...block,
      isLast: continuation ? false : block.isLast,
      data: {
        ...block.data,
        index: continuation ? '' : block.data?.index,
        question,
        options: isFinal ? block.data?.options : null,
        needPlaceholder: isFinal ? block.data?.needPlaceholder : false,
      },
    }
  }

  /**
   * 双栏中的超长题改为整行再拆。
   * @param block 双栏块
   */
  private async splitQuestionColumn(block: PaperBlock) {
    for (const item of block.items ?? []) {
      if (!item.html) continue
      await this.splitColumnItem(block, item)
    }
  }

  /**
   * 单题整行跨页。
   * @param block 原双栏块
   * @param item 其中一道题
   */
  private async splitColumnItem(block: PaperBlock, item: ColumnItem) {
    const nodes = htmlFragments(item.html)
    if (nodes.length < 2) {
      const page = this.pages[this.index]
      if (page.length > 0 && page[page.length - 1].type === 'block_title') {
        const title = page.pop()
        this.pages.push(title ? [title] : [])
        this.index += 1
        this.commit()
      }
      const whole = this.buildColumnBlock(block, item, item.html, false, true)
      this.pages[this.index].push(whole)
      await waitForBlockImagesLoaded(whole)
      this.commit()
      await this.measureCurrent()
      return
    }

    let start = 0
    let firstPart = true
    while (start < nodes.length) {
      let lo = start + 1
      let hi = nodes.length
      let best = start
      while (lo <= hi) {
        const mid = (lo + hi) >> 1
        const trial = this.buildColumnBlock(
          block,
          item,
          nodes.slice(start, mid).join(''),
          !firstPart,
          mid === nodes.length,
        )
        const height = await this.heightWith(trial)
        if (height <= this.containerHeight) {
          best = mid
          lo = mid + 1
        } else {
          hi = mid - 1
        }
      }
      if (best === start) {
        if (this.pages[this.index].length > 0) {
          await this.pushPage()
          continue
        }
        best = start + 1
      }
      const piece = this.buildColumnBlock(
        block,
        item,
        nodes.slice(start, best).join(''),
        !firstPart,
        best === nodes.length,
      )
      this.pages[this.index].push(piece)
      await waitForBlockImagesLoaded(piece)
      this.commit()
      await this.measureCurrent()
      start = best
      firstPart = false
      if (start < nodes.length) await this.pushPage()
    }
  }

  /**
   * 生成单题整行块。
   * @param block 原块
   * @param item 题目
   * @param html 当前页 HTML
   * @param continuation 是否续页
   * @param isFinal 是否最后一段
   */
  private buildColumnBlock(
    block: PaperBlock,
    item: ColumnItem,
    html: string,
    continuation: boolean,
    isFinal: boolean,
  ): PaperBlock {
    return {
      ...block,
      fullRow: true,
      items: [
        {
          ...item,
          index: continuation ? '' : item.index,
          html,
          options: isFinal ? item.options : null,
        },
      ],
    }
  }

  /**
   * 两题一行；放不下时改成整行。
   * @param items 左右两题
   * @param type 模块类型
   * @param isFirst 是否模块首行
   * @returns 还没排上、需要重新配对的题目
   */
  private async addColumnPair(
    items: ColumnItem[],
    type: BlockTypeName,
    isFirst: boolean,
  ): Promise<ColumnItem[]> {
    const row: PaperBlock = { type, items, isLast: isFirst }
    const rowHeight = await this.heightWith(row)
    if (rowHeight <= this.containerHeight) {
      await this.add(row)
      return []
    }
    if (this.pages[this.index].length > 0) {
      await this.add(row)
      return []
    }

    const first: PaperBlock = {
      type,
      items: [items[0], { index: '', html: '' }],
      fullRow: true,
      isLast: isFirst,
    }
    const firstHeight = await this.heightWith(first)
    if (firstHeight > this.containerHeight) {
      await this.add(first)
      return items[1]?.html ? [items[1]] : []
    }
    await this.add(first)
    await this.add({
      type,
      items: [items[1], { index: '', html: '' }],
      fullRow: true,
      isLast: false,
    })
    return []
  }

  /**
   * 核心知识点 / 巩固记忆按两列往下排。
   * @param items 题目
   * @param type 模块类型
   */
  async addColumns(items: ColumnItem[], type: BlockTypeName) {
    const queue = items.filter((item) => item.html)
    let isFirst = true
    while (queue.length >= 2) {
      const pair = queue.splice(0, 2)
      const rest = await this.addColumnPair(pair, type, isFirst)
      isFirst = false
      if (rest.length > 0) queue.unshift(...rest)
    }
    if (queue.length === 1) {
      await this.add({
        type,
        items: [queue[0], { index: '', html: '' }],
        isLast: isFirst,
      })
    }
  }

  /**
   * 去掉空页并同步最终结果。
   */
  prune() {
    this.pages = pruneEmptyPaperPages(this.pages)
    this.index = Math.max(0, this.pages.length - 1)
    this.commit()
  }
}
